<script>
export default {
  name: "DidoResourcesPagination",
};
</script>

<script setup>
import { ref, computed } from "vue";
import DidoResourcePaginated from "./Resource.vue";
import { pause } from "../../../utils";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  resources: { type: Array, required: true },
  type: { type: String, required: true },
  pageSize: { type: Number, required: false, default: 5 },
});

// define events emitted
const emit = defineEmits(["datafile-selection"]);

// define reactive datas
const pageOfResources = ref(1);

// define ref for dom reference
const paginationNode = ref(null);

// define computed data for label counter
const labelCounter = computed(() => {
  const labelTypeResource = {
    datafiles: (n) => {
      if (n === 0) return "Aucun fichier de données";
      if (n === 1) return "1 fichier de données";
      return `${n} fichiers de données`;
    },
    documentations: (n) => {
      if (n === 0) return "Aucune documentation";
      if (n === 1) return "1 documentation";
      return `${n} documentations`;
    },
    refs: (n) => {
      if (n === 0) return "Aucun référentiel ou nomenclature";
      if (n === 1) return "1 référentiel ou nomenclature";
      return `${n} référentiels ou nomenclatures`;
    },
    historicalDatas: (n) => {
      if (n === 0) return "Aucun fichier de données historiques";
      if (n === 1) return "1 fichier de données historiques";
      return `${n} fichiers de données historiques`;
    },
  };
  return labelTypeResource[props.type](props.resources.length);
});

// define computed data for page max
const maxPage = computed(() =>
  Math.ceil(props.resources.length / props.pageSize),
);

// define computed data for resources in page
const resourcesInPage = computed(() => {
  return props.resources.slice(
    props.pageSize * (pageOfResources.value - 1),
    props.pageSize * pageOfResources.value,
  );
});

// define function for setting focus on pagination element after interaction with pagination buttons
const scrollToPaginationNode = async () => {
  await pause(1);
  paginationNode.value?.$el.scrollIntoView({
    block: "center",
    behavior: "auto",
  });
};
</script>

<template>
  <div>
    <div class="dido-dataset-resources-pagination-title">
      {{ labelCounter }}
    </div>
    <div class="q-mt-lg">
      <template
        v-for="(resource, index) of resourcesInPage"
        :key="`resource-${props.type}-page_${pageOfResources}-${index}`"
      >
        <DidoResourcePaginated
          :api-url="props.apiUrl"
          :resource="resource"
          :type="props.type"
          @datafile-selection="
            emit('datafile-selection', { datafileRid: resource.rid })
          "
        />
      </template>
    </div>
    <div class="row-all-center">
      <QPagination
        v-if="maxPage > 1"
        ref="paginationNode"
        v-model="pageOfResources"
        boundary-links
        direction-links
        :max="maxPage"
        size="12px"
        unelevated
        color="secondary"
        active-design="flat"
        active-color="secondary"
        class="text-center"
        @update:model-value="scrollToPaginationNode"
      />
    </div>
  </div>
</template>

<style lang="sass" scoped>
// Dataset resources pagination title
.dido-dataset-resources-pagination-title
  font-size: 1.4em
  font-weight: 500
</style>
